import { createFileRoute, Link } from "@tanstack/react-router";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { RelatedChannels } from "@/components/calv/RelatedChannels";
import { CTABlock } from "@/components/calv/CTABlock";
import { Callout } from "@/components/calv/Callout";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { jsonLdScript, serviceSchema, breadcrumbSchema, pageHead } from "@/lib/seo";

export const Route = createFileRoute("/en/services/performance-marketing/meta-ads/")({
  head: () => {
    const h = pageHead("pmMetaAds", "en");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
        jsonLdScript(serviceSchema({
          name: "Meta Ads Agency",
          description: "Facebook, Instagram, Advantage+ managed with creative discipline, clean attribution, and proprietary tooling for bulk operations.",
          url: "/en/services/performance-marketing/meta-ads",
        })),
        jsonLdScript(breadcrumbSchema([
          { name: "Home", path: "/en" },
          { name: "Services", path: "/en/services" },
          { name: "Performance Marketing", path: "/en/services/performance-marketing" },
          { name: "Meta Ads", path: "/en/services/performance-marketing/meta-ads" },
        ])),
      ],
    };
  },
  component: MetaAdsEN,
});

const steps = [
  { num: "01", title: "Analysis (3–5 days)", body: "Account audit, creative performance analysis, Conversions API setup check, data quality audit (match quality scores), pixel hygiene check. Output: concrete hypotheses where efficiency is lost — and whether it's predominantly a creative, tracking, or structural problem. Clear diagnosis, not generic audit report." },
  { num: "02", title: "Setup, Hardening & First Creatives (1–2 weeks)", body: "Hardening Conversions API via server-side tracking. Checking Events Manager for domain verification and aggregated events configuration. Aligning account structure to economic logic — top-of-funnel, retargeting stages, CLV segmentation. In parallel, setting up first creative test batches via picazzoo — in Meta, creative iteration starts on day one, not later." },
  { num: "03", title: "Creative Iteration in Weekly Cadence (from week 2–3)", body: "Weekly creative test cadence with clear hypotheses. picazzoo for scalable asset variation. Iteration on hook, format, and audience-match variation. In the Meta ecosystem, speed of creative iteration is the main factor for performance scaling — whoever iterates weekly beats whoever iterates monthly." },
  { num: "04", title: "Scaling & Ongoing Operations (continuous)", body: "Budget scaling in working audience-creative combinations. Advantage+ selectively where data quality supports it. Monthly performance review on economic metrics, not platform KPIs. Operational management directly by the managing director — not by junior account managers with heavy mandate loads." },
];

const capabilities = [
  { name: "Conversions API & Tracking", detail: "Server-side · Match Quality" },
  { name: "Creative Test Pipelines", detail: "Hypothesis logic · learning cycles" },
  { name: "Asset Production with picazzoo", detail: "AI · brand-true · scalable" },
  { name: "Account Ops with GonzalAds", detail: "Bulk · automation rules" },
  { name: "Advantage+ Shopping selectively", detail: "Where data quality supports it" },
  { name: "Audience Strategies", detail: "Lookalikes · Custom · CRM" },
  { name: "Funnel Architecture", detail: "Awareness · Consideration · Conversion" },
  { name: "Reporting & Management", detail: "Economic + platform diagnostics" },
];

const audience = [
  { tag: "Primary", title: "B2C E-Commerce", body: "Most important social performance channel for most e-commerce setups, especially with visually communicable products (fashion, cosmetics, home, sports, pet). Scalable from €1,500/month to six-figure budgets." },
  { tag: "Secondary", title: "B2C SaaS", body: "Works for SaaS products serving a broad consumer audience — apps, tools, subscriptions in the lifestyle space. Trial sign-up must be cleanly tracked." },
  { tag: "Limited", title: "B2B with visual leverage", body: "Works for B2B setups when the audience is also privately active and the product is visually communicable. For classic enterprise B2B, LinkedIn is significantly better suited." },
  { tag: "Disqualification", title: "When we are not the right partner", body: "Monthly budgets under €1,500 — operational effort for clean setup is high. Expectation 'viral posts' — we do performance, not influencer management. Without strong visuals, Meta is rarely the right choice." },
];

const realistic = [
  "ROAS improvements of 30–60% in B2C e-commerce within 3–4 months are realistic when previously working with unhardened Conversions API and without systematic creative iteration.",
  "Match Quality Score improvements from 6–7 to 8–9 are achievable in most setups — and translate directly to better bid-algorithm performance.",
  "Operational time reduction of 70–90% in asset production and campaign setup through use of picazzoo and GonzalAds.",
  "Scaling leverage of 3–5x at the same ROAS is possible when the creative pipeline works and customer economics support it.",
];

const value = [
  ["Creative pipeline instead of designer bottleneck.", "Through picazzoo we produce brand-true asset variations in quantities not economically achievable in traditional designer workflows."],
  ["Account operations 80–90% faster.", "Through GonzalAds we build campaigns, asset sets, and automation rules in a fraction of the time native Ads Manager requires."],
  ["Clean attribution despite iOS data loss.", "Server-side Conversions API with active match-quality monitoring. No 'set up once, then hope' logic."],
  ["Economic management instead of platform vanity.", "We report on contribution margin and customer LTV, not on CTRs or platform ROAS."],
  ["Operational management by experienced hand.", "Directly by the managing director — visible in response time and strategic depth."],
  ["Compensation model without budget coupling.", "We don't earn more when you spend more."],
];

const related = [
  { to: "/en/services/performance-marketing/google-ads", name: "Google Ads", sub: "Search, Shopping, and PMax as a complementary performance channel" },
  { to: "/en/services/picazzoo", name: "picazzoo", sub: "Our AI pipeline for brand-true visual assets" },
  { to: "/en/services/conversion-rate-optimization", name: "Conversion Rate Optimization", sub: "Conversion leverage on landing pages for higher Meta profitability" },
];

const channelLinks = [
  { to: "/en/services/performance-marketing/tiktok-ads", name: "TikTok Ads", sub: "Performance on TikTok beyond the influencer logic." },
  { to: "/en/services/performance-marketing/youtube-demand-gen", name: "YouTube Demand Gen", sub: "Video performance on YouTube for awareness and mid-funnel conversion." },
  { to: "/en/services/performance-marketing/google-ads", name: "Google Ads", sub: "Search, Shopping, PMax and Demand Gen as a complementary performance channel." },
  { to: "/en/services/performance-marketing/affiliate-marketing", name: "Affiliate Marketing", sub: "Partner-driven performance with its own tracking and commission discipline." },
];

function MetaAdsEN() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="Performance Marketing · Meta Ads"
          headline={<>Meta Ads Agency — <em>performance beyond platform logic,</em> with creative discipline.</>}
          lead="Facebook, Instagram, Advantage+ — managed with creative discipline, server-side tracking, and proprietary bulk tooling. Calvarius has worked as a Meta Ads agency for e-commerce, B2C SaaS, and visually communicable B2B setups since 2013 — without boost-button mentality, without platform myths."
          metaStrip={["In the channel since 2013", "Up to €100K/month budgets", "Own bulk tool"]}
          breadcrumbs={[
            { label: "Home", to: "/en" },
            { label: "Services", to: "/en/services" },
            { label: "Performance Marketing", to: "/en/services/performance-marketing" },
            { label: "Meta Ads" },
          ]}
        />

        <Section number="02" kicker="Standpoint" headline={<>What a Meta Ads agency must <em>truly deliver today.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Anyone looking for a Meta Ads agency usually has a clear experience with the platform — and most often a mixed one. Meta has drastically reduced its controllability in recent years. iOS 14.5 halved data quality, Advantage+ Shopping takes manual control away, and the platform aggressively recommends ad-set consolidations economically sensible for Meta but not for many advertisers. At the same time, Meta remains indispensable for most B2C e-commerce mandates — the reach, audience quality, and scaling possibilities aren't found in any other social media channel.</p>
              <p className="mb-5">Calvarius works in the Meta ecosystem with three disciplines that make the difference in most mandates. First: <strong className="font-semibold text-ink">creative discipline</strong> as the main control lever — creatives are today the most important performance factor, more important than targeting, more important than bid strategies. Second: <strong className="font-semibold text-ink">clean attribution</strong> despite iOS data loss — Conversions API, server-side Events Manager integration, and match-quality optimization are mandatory, not optional. Third: <strong className="font-semibold text-ink">account structure</strong> that doesn't blindly follow platform recommendations — Advantage+ is a tool, not a creed.</p>
              <p>Concretely: we produce creative variants in brand voice via <Link to="/en/services/picazzoo" className="underline">picazzoo</Link> — AI-driven but brand-true, in quantities not achievable in traditional designer workflows. We manage account operations through <Link to="/en/services/gonzalads" className="underline">GonzalAds</Link>, which enables bulk operations, asset management, and automation rules at speeds not possible in the native Ads Manager. And we track via server-side Conversions API with match-quality scores we actively monitor — not "we set it up, so it runs."</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="03" kicker="Audience fit" headline={<>When you need a Meta Ads agency — <em>and when you don't.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2">
              {audience.map((a) => (
                <div key={a.title} className="bg-white p-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">{a.tag}</div>
                  <div className="mt-3 font-display text-xl text-ink">{a.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{a.body}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="04" kicker="Methodology" headline={<>How our Meta Ads agency <em>starts mandates.</em></>}>
          <ProcessFlow steps={steps} />
          <RevealOnScroll>
            <div className="mt-10 max-w-2xl rounded-lg border border-rule bg-bg-subtle p-7">
              <p className="eyebrow mb-3">Fast start as competitive advantage</p>
              <p className="text-[15px] leading-relaxed text-ink-2">While classic large agencies often need four to eight weeks before the first creative iteration runs, we are productive with weekly test cycles from week two to three. This is possible because our tooling — picazzoo for asset production, GonzalAds for account operations — eliminates the classic designer and account-manager bottleneck, and because the managing director works directly operationally, not through stakeholder layers.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="05" kicker="What we deliver" headline={<>What our Meta Ads agency <em>concretely delivers.</em></>}>
          <Capabilities items={capabilities} />
        </Section>

        <Section number="06" kicker="Tooling" headline={<>Tools and stack of our <em>Meta Ads management.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Native platform</p>
                <ul className="space-y-2 text-sm text-ink-2">
                  <li>Meta Ads Manager (selectively)</li>
                  <li>Meta Business Suite</li>
                  <li>Events Manager</li>
                  <li>Pixel Helper</li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Tracking & analytics</p>
                <ul className="space-y-2 text-sm text-ink-2">
                  <li>Server-side Conversions API</li>
                  <li>Google Tag Manager (Web + Server)</li>
                  <li>Google Analytics 4</li>
                  <li>Match Quality Score Monitoring</li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Calvarius tools</p>
                <ul className="space-y-3 text-sm text-ink-2">
                  <li><Link to="/en/services/gonzalads" className="font-medium text-ink hover:text-cyan-deep">GonzalAds →</Link><br/><span className="text-ink-3">Speed tool for Meta Ads operations</span></li>
                  <li><Link to="/en/services/picazzoo" className="font-medium text-ink hover:text-cyan-deep">picazzoo →</Link><br/><span className="text-ink-3">Visual production pipeline with brand DNA</span></li>
                </ul>
              </div>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="07" kicker="What's realistic" headline={<>Realistic results of a <em>Meta Ads optimization.</em></>}
          caption="These ranges are empirical. Depending on starting situation, leverage may be larger or smaller.">
          <RevealOnScroll>
            <ul className="space-y-4">
              {realistic.map((r, i) => (
                <li key={i} className="flex gap-4 rounded-lg border border-rule bg-white p-6">
                  <span className="font-mono text-xs text-cyan-deep">{String(i+1).padStart(2,"0")}</span>
                  <p className="text-[15px] leading-relaxed text-ink-2">{r}</p>
                </li>
              ))}
            </ul>
          </RevealOnScroll>
        </Section>

        <Section number="08" kicker="What you gain" headline={<>What you get from our <em>Meta Ads agency.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-2">
              {value.map(([h, b]) => (
                <div key={h} className="bg-white p-7">
                  <div className="font-display text-lg text-ink">{h}</div>
                  <p className="mt-2 text-sm leading-relaxed text-ink-3">{b}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <RelatedChannels locale="en" items={channelLinks} />

        <Section variant="tinted" number="09" kicker="Go deeper" headline={<>Related disciplines.</>}
          caption="These topics work in interplay.">
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              {related.map((r) => (
                <Link key={r.to} to={r.to} className="group flex flex-col rounded-lg border border-rule bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-lime hover:shadow-[0_12px_40px_-20px_oklch(0.85_0.18_125/0.5)]">
                  <h3 className="font-display text-xl font-semibold text-ink">{r.name}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-3">{r.sub}</p>
                  <span aria-hidden className="mt-5 font-mono text-xs uppercase tracking-wider text-ink-2 group-hover:text-lime-deep">→</span>
                </Link>
              ))}
            </div>
            <div className="mt-8">
              <Link to="/en/services/performance-marketing" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">
                ← All performance marketing disciplines
              </Link>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="10" kicker="Special application" headline={<>Special application: <em>Employee recruitment.</em></>}>
          <RevealOnScroll>
            <div className="max-w-2xl">
              <p className="text-[17px] leading-relaxed text-ink-2">
                Recruiting via Meta Ads has been an independent discipline within Calvarius mandates since 2018 — with its own targeting mechanics, its own funnel requirements, and industry-specific cost-per-application benchmarks. For mid-market mandates in care, skilled trades, logistics, manufacturing, hospitality, and education, Meta Ads is the most economically rational recruiting infrastructure — with correct targeting, 60–90 percent lower CPA than classical job portals.
              </p>
              <Link
                to="/en/services/performance-marketing/meta-ads/recruiting"
                className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline"
              >
                More about Meta Ads for employee recruitment →
              </Link>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="11" kicker="Get concrete">
          <Callout>
            <strong>In strategic context:</strong> Meta Ads is the performance tactic for paid reach on Instagram and Facebook. Anyone looking for the <em>integrated</em> social methodology — organic content, paid amplification, and community substance as one system — will find it under <Link to="/en/services/social-media-marketing" className="underline">Social Media Marketing →</Link>.
          </Callout>
          <CTABlock
            headline={<>Meta Ads audit in 30 minutes — <em>free and non-binding.</em></>}
            body="In a first conversation we clarify whether Meta Ads makes economic sense in your setup — and if so, with what priority. Free, non-binding, no sales pitch."
            primary={{ label: "Schedule a conversation", to: "/en/contact" }}
            secondary={{ label: "Performance marketing overview", to: "/en/services/performance-marketing" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
